import React, { useState, useEffect } from "react";
import Modal from "../UIElements/Modal";
import { Input } from "../UIElements/Input";
import { Button } from "../UIElements/Button";
import { Icon } from "../";
import { PhoneInputSimple } from "../UIElements/PhoneInput";
import { useUpdateDriverManager } from "../../../hooks/useDriverManagers";

const EditDriverManagerModal = ({ isOpen, onClose, user }) => {
  const updateDriverManagerMutation = useUpdateDriverManager();
  const [formData, setFormData] = useState({
    name: "",
    lastname: "",
    email: "",
    phone: "",
    password: "",
    photo: null,
  });
  const [phoneData, setPhoneData] = useState({
    countryCode: "",
    phoneNumber: "",
    fullNumber: "",
  });
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);

  // Load user data when modal opens or user changes
  useEffect(() => {
    if (isOpen && user) {
      const fullPhone =
        user.country_phone_code && user.cellphone
          ? `+${user.country_phone_code}${user.cellphone}`
          : "";

      setFormData({
        name: user.name || "",
        lastname: user.lastname || "",
        email: user.email || "",
        phone: fullPhone,
        password: "", // Don't pre-fill password
        photo: user.photo || null,
      });

      setPhoneData({
        countryCode: user.country_phone_code || "",
        phoneNumber: user.cellphone || "",
        fullNumber: fullPhone,
      });

      setImagePreview(user.photo || null);
      setErrors({});
    }
  }, [isOpen, user]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handlePhoneDataChange = (data) => {
    setPhoneData(data);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setErrors((prev) => ({
          ...prev,
          photo: "Please select a valid image file",
        }));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          photo: "Image size must be less than 5MB",
        }));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setFormData((prev) => ({
          ...prev,
          photo: base64,
        }));
        setImagePreview(base64);
        setErrors((prev) => ({
          ...prev,
          photo: null,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.lastname.trim()) {
      newErrors.lastname = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\+\d{10,15}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number with country code";
    }

    // Password is optional for editing, but if provided, must be at least 8 characters
    if (formData.password.trim() && formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const countryCode =
        phoneData.countryCode || user?.country_phone_code || "58";
      const cellphone = phoneData.phoneNumber || formData.phone;

      // Build payload with only non-empty values
      const payload = {};

      // Required fields (already validated, so they should have values)
      const trimmedName = formData.name.trim();
      const trimmedLastname = formData.lastname.trim();
      const trimmedEmail = formData.email.trim();

      if (trimmedName) payload.name = trimmedName;
      if (trimmedLastname) payload.lastname = trimmedLastname;
      if (trimmedEmail) payload.email = trimmedEmail;
      if (countryCode) payload.country_phone_code = countryCode;
      if (cellphone) payload.cellphone = cellphone;

      // Optional fields - only include if they have values or have changed
      const trimmedPassword = formData.password.trim();
      if (trimmedPassword) {
        payload.password = trimmedPassword;
      }

      // Photo - only include if it's a new base64 string (starts with data:image/)
      if (
        formData.photo &&
        typeof formData.photo === "string" &&
        formData.photo.startsWith("data:image/")
      ) {
        payload.photo = formData.photo;
      } else if (formData.photo === null && user?.photo) {
        // If photo was removed (set to null and user had a photo), send null
        payload.photo = null;
      }

      await updateDriverManagerMutation.mutateAsync({
        userId: user.id,
        userData: payload,
      });

      handleClose();
    } catch (error) {
      console.error("Update driver manager error:", error);
    }
  };

  const handleClose = () => {
    if (user) {
      const fullPhone =
        user.country_phone_code && user.cellphone
          ? `+${user.country_phone_code}${user.cellphone}`
          : "";

      setFormData({
        name: user.name || "",
        lastname: user.lastname || "",
        email: user.email || "",
        phone: fullPhone,
        password: "",
        photo: user.photo || null,
      });

      setPhoneData({
        countryCode: user.country_phone_code || "",
        phoneNumber: user.cellphone || "",
        fullNumber: fullPhone,
      });

      setImagePreview(user.photo || null);
    } else {
      setFormData({
        name: "",
        lastname: "",
        email: "",
        phone: "",
        password: "",
        photo: null,
      });
      setPhoneData({
        countryCode: "",
        phoneNumber: "",
        fullNumber: "",
      });
      setImagePreview(null);
    }
    setErrors({});
    onClose();
  };

  if (!user) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Driver Manager"
      subtitle="Update the driver manager information"
      maxWidth="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Photo and Name Fields */}
        <div className="flex items-end space-x-6">
          {/* Profile Photo - 40% width */}
          <div className="w-2/5 max-w-40">
            <div className="space-y-3">
              <div className="flex justify-center">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      className="h-24 w-24 rounded-full border-2 border-gray-700 object-cover"
                      src={imagePreview}
                      alt="Profile preview"
                    />
                    {/* Overlay - Dots and Image Icon - Always visible */}
                    <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                      <div className="flex items-center space-x-1">
                        <Icon name="Image" className="h-5 w-5 text-white" />
                        <Icon
                          name="MoreVertical"
                          className="h-5 w-5 text-white"
                        />
                      </div>
                    </div>
                    {/* Clickable overlay */}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                    />
                  </div>
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gray-700">
                    <Icon name="User" className="h-10 w-10 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  id="photo-upload"
                />
                <label
                  htmlFor="photo-upload"
                  className="block w-full cursor-pointer rounded-lg border border-gray-600 bg-gray-700 px-3 py-2 text-center text-sm text-gray-300 transition-colors hover:bg-gray-600"
                >
                  {imagePreview ? "Change Photo" : "Select Photo"}
                </label>
              </div>
              {errors.photo && (
                <p className="text-center text-xs text-red-500">
                  {errors.photo}
                </p>
              )}
            </div>
          </div>

          {/* Name Fields - 60% width */}
          <div className="flex-1 space-y-4">
            <Input
              label="First Name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={errors.name}
              variant="dark"
              placeholder="Enter first name"
              required
            />
            <Input
              label="Last Name"
              value={formData.lastname}
              onChange={(e) => handleInputChange("lastname", e.target.value)}
              error={errors.lastname}
              variant="dark"
              placeholder="Enter last name"
              required
            />
          </div>
        </div>

        {/* Email */}
        <Input
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          error={errors.email}
          variant="dark"
          placeholder="Enter email address"
          required
        />

        {/* Phone Field */}
        <PhoneInputSimple
          label="Phone Number"
          value={formData.phone}
          onChange={(value) => handleInputChange("phone", value || "")}
          onPhoneDataChange={handlePhoneDataChange}
          error={errors.phone}
          placeholder="Enter phone number"
          size="md"
        />

        {/* Password */}
        <Input
          label="Password (Optional)"
          type="password"
          value={formData.password}
          onChange={(e) => handleInputChange("password", e.target.value)}
          error={errors.password}
          variant="dark"
          placeholder="Leave blank to keep current password"
        />

        {/* User Type Info */}
        <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-4">
          <div className="flex items-center">
            <Icon name="UserCog" className="mr-2 h-5 w-5 text-blue-400" />
            <div>
              <h4 className="text-sm font-medium text-blue-300">User Type</h4>
              <p className="mt-1 text-sm text-blue-200">
                This user is a Driver Manager
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 border-t border-gray-800 pt-4 sm:flex-row">
          <Button
            type="button"
            variant="default"
            onClick={handleClose}
            className="flex-1 bg-gray-800 sm:flex-none"
            disabled={updateDriverManagerMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={updateDriverManagerMutation.isPending}
            className="w-full bg-blue-600 text-white hover:bg-blue-700 sm:w-auto"
            disabled={updateDriverManagerMutation.isPending}
          >
            {updateDriverManagerMutation.isPending ? "Updating..." : "Update"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditDriverManagerModal;
